import * as THREE from 'three';
import GUI from 'lil-gui';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { createRolePairing } from '../../ui/rolePairing.js';
import { applyRoleOverrides, type RoleGroupsJson } from '../../materials/applyRoleOverrides.js';
import { applyPaintLinkSync, type PaintLinkState } from '../../materials/paintOverrides.js';
import { applyTireAO } from '../../materials/applyTireAO.js';
import { createShadowPlane } from '../../scene/firstPaint.js';
import { stripMayaNamespace } from '../../utils/mayaNamespace.js';
import { LEXUS_NX_PALETTE } from '../../ui/paintSwatches.js';
import type { ViewerTool } from './tool.js';
import type { ViewerContext } from '../context.js';

const BASE = import.meta.env.BASE_URL;

export const pairingTool: ViewerTool = {
  id: 'pairing',
  enabled: (ctx) => ctx.params.get('pair') === '1' && ctx.rolesOn && !!ctx.config.pairReference && !!ctx.config.groups,
  async setup(ctx: ViewerContext): Promise<void> {
    const ref = ctx.config.pairReference!;
    const loader = ctx.gltfLoader as GLTFLoader;

    // 차(RAV4) 의 role-groups 재fetch (baseline 변경 대상)
    const ravRes = await fetch(`${BASE}${ctx.config.groups}?t=${Date.now()}`);
    const ravJson = (await ravRes.json()) as RoleGroupsJson;

    // LX 레퍼런스 로드
    const lxGltf = await loader.loadAsync(BASE + ref.glb);
    const lxGroup = lxGltf.scene;
    lxGroup.name = 'lx_O';
    lxGroup.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) return;
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      for (const m of mats) if (m) m.name = stripMayaNamespace(m.name);
    });
    const lxRes = await fetch(`${BASE}${ref.groups}?t=${Date.now()}`);
    const lxJson = (await lxRes.json()) as RoleGroupsJson;
    const lxBaseline = lxJson.baseline;
    applyRoleOverrides(lxGroup, lxJson, ctx.roleDefs);
    const lxPaintState: PaintLinkState = {
      current: ctx.saved.currentPaint ?? LEXUS_NX_PALETTE[0]!.hex,
      overrides: new Map(Object.entries(ctx.saved.paintOverrides)),
    };
    applyPaintLinkSync(lxGroup, ctx.roleDefs, lxPaintState);
    await applyTireAO(lxGroup, lxGroup, loader);
    lxGroup.traverse((o) => { o.userData['skipPaint'] = true; });

    const group = ctx.root;
    const ravBox = new THREE.Box3().setFromObject(group);
    const lxBox = new THREE.Box3().setFromObject(lxGroup);
    const gap = 1.0;
    lxGroup.position.x = -((ravBox.max.x - ravBox.min.x) / 2 + (lxBox.max.x - lxBox.min.x) / 2 + gap);
    ctx.scene.add(lxGroup);

    const lxShadowTex = await ctx.shadowTexPromise;
    const lxWorldBox = new THREE.Box3().setFromObject(lxGroup);
    ctx.scene.add(createShadowPlane(lxShadowTex, lxWorldBox, lxWorldBox.min.y));

    const both = new THREE.Box3().setFromObject(group).union(new THREE.Box3().setFromObject(lxGroup));
    ctx.controls.fitToBox(both, false);

    const gui = new GUI({ title: 'RAV4 Pairing' });
    createRolePairing({
      scene: ctx.scene, camera: ctx.camera, domElement: ctx.renderer.domElement,
      lxGroup, ravGroup: group, roleDefs: ctx.roleDefs, ravJson, lxBaseline, gui, invalidate: ctx.invalidate,
    });
    ctx.invalidate();
  },
};
